/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { DesktopCommand } from './types/commands';
import { IDesktopContextProps } from './types/context-props';
import { DesktopMessage } from './types/messages';

const DESKTOP_APP_NAME = 'RevenueWellDesktop';

export const DesktopContext = createContext<IDesktopContextProps>(null!);

export function DesktopProvider(props: React.PropsWithChildren<{ isDesktopWidget: boolean }>) {
  const messageHandlers = useRef<{ type: DesktopMessage['type']; callback: (cb: DesktopMessage) => void }[]>([]);

  useEffect(() => {
    if (isDesktopApplication()) {
      sendCommand({ type: 'desktop-listen-messages' });
      ipcRenderer()?.on('desktop-forward-message', (e: any, args: any) => {
        messageHandlers.current.forEach((h) => {
          if (args.type === h.type) h.callback(args);
        });
      });
    }
  }, []);

  const ipcRenderer = () => {
    try {
      return window.require('electron').ipcRenderer;
    } catch (ex) {
      return null;
    }
  };

  const isDesktopApplication = () => isDesktopUserAgent(DESKTOP_APP_NAME);

  const isNewDesktopApp = () => {
    const isDesktopAgent = isDesktopUserAgent(DESKTOP_APP_NAME);

    if (!isDesktopAgent) return false;

    const match = navigator.userAgent.match(/Electron\/(\d+)/);
    if (!match || !match[1]) return false;

    const electronMajorVersion = parseInt(match[1]);
    return !isNaN(electronMajorVersion) && electronMajorVersion >= 23;
  };

  const isDesktopUserAgent = (text: string) => {
    return (
      typeof navigator === 'object' &&
      typeof navigator.userAgent === 'string' &&
      navigator.userAgent.indexOf(DESKTOP_APP_NAME) >= 0 &&
      ipcRenderer() !== null
    );
  };

  const listenMessage = (type: DesktopMessage['type'], callback: (cb: DesktopMessage) => void) => {
    messageHandlers.current.push({ type, callback });
    return function unsubscribe() {
      const idx = messageHandlers.current.findIndex((x) => x.callback === callback);
      if (idx >= 0) messageHandlers.current.splice(idx, 1);
    };
  };

  // send a Message to Main Renderer Window or other Windows (for example widgets)
  const sendMessage = (message: DesktopMessage) => {
    if (isDesktopApplication()) sendCommand({ type: 'desktop-send-message', payload: message });
  };

  // send a Command to the Desktop Main Process
  const sendCommand = (command: DesktopCommand) => {
    if (isDesktopApplication()) ipcRenderer()?.send(command.type, command.payload);
  };

  const context = {
    isDesktopApplication,
    isNewDesktopApp,
    isDesktopWidget: props.isDesktopWidget,
    listenMessage,
    sendMessage,
    downloadFile: (url: string) => sendCommand({ type: 'desktop-download-file', payload: {url} }),
    showMainWindow: () => sendCommand({ type: 'desktop-show-main-window' }),
    showWindow: () => sendCommand({ type: 'desktop-show-window' }),
    hideWindow: () => sendCommand({ type: 'desktop-hide-window' }),
    resizeWindow: (heightMargin: number, height: number) =>
      sendCommand({ type: 'desktop-resize-window', payload: { heightMargin, height } }),
    clearCache: () => sendCommand({ type: 'desktop-clear-cache' }),
    openUrlInMainWindow: (url: string) => sendCommand({ type: 'desktop-open-url-in-main-window', payload: { url } }),
    openUrlInBrowser: (url: string) => sendCommand({ type: 'desktop-open-url-in-browser', payload: { url } }),
    startCall: (number: string | number) => sendMessage({ type: 'desktop-start-call', payload: { number } }),
    startLwpCall: (number: string | number) => sendMessage({ type: 'desktop-start-lwp-call', payload: { number } }),
    startContactCall: (contactId: string | number) =>
      sendMessage({ type: 'desktop-start-call-contact', payload: { contactId } }),
    openWrapperPatientProfile: (phoneNumber: string, locationId: number) =>
        sendMessage({ type: 'desktop-wrapper-patient-profile', payload: { phoneNumber, locationId } }),
    setUnreadBadge: (unread: string | number | null) =>
      sendCommand({ type: 'desktop-set-unread-badge', payload: { unread } })
  } as IDesktopContextProps;

  return <DesktopContext.Provider value={{ ...context }}>{props.children}</DesktopContext.Provider>;
}

export function useDesktop() {
  const context = useContext(DesktopContext);

  if (!context) {
    throw new Error('useDesktop must be used within the DesktopProvider');
  }

  return context;
}
